import {
	format,
	isToday,
	isYesterday,
	addDays as addDaysFns,
	addMonths as addMonthsFns,
	addYears as addYearsFns,
	addMinutes as addMinutesFns,
	isValid as isValidFns,
	getQuarter as getQuarterFns,
	parseISO,
	differenceInMinutes,
} from 'date-fns';
import { sv, enUS } from 'date-fns/locale';
import { t } from 'i18next';
import { getCultureCode } from '../Services/common';

export function getLocale() {
	const cultureCode = getCultureCode();
	if (cultureCode == 'en-US') {
		return enUS;
	}

	return sv;
}

export function isValidDateString(date: string): boolean {
	return isValidDate(parseISO(date));
}

export function isValidDate(date?: Date): boolean {
	return isValidFns(date);
}

export function formatToSimpleTimeString(date: Date, excludePadding = false) {
	return format(date, excludePadding ? 'H:mm' : 'HH:mm');
}

export function formatToSimpleDateTimeString(date: Date) {
	return format(date, 'yyyy-MM-dd HH:mm:ss');
}

export function formatToSimpleDateString(date: Date | string | undefined) {
	if (!date) return '';
	try {
		return format(typeof date === 'string' ? parseISO(date) : date, 'yyyy-MM-dd');
	} catch (error) {
		console.error('Error formatting date', date, error);
		return '';
	}
}

export function formatToDayMonthString(date: Date) {
	return format(date, 'dd/M');
}

export function formatDate(date: Date, formatString: string) {
	return format(date, formatString, { locale: getLocale() });
}

/** Formats a Date to 'YYYY-MM-DD' using local date parts (avoids UTC shift from toISOString). */
export function formatLocalDate(date: Date): string {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function parseStringToDate(date: string) {
	return parseISO(date);
}

export function addMinutes(date: Date, amount: number) {
	return addMinutesFns(date, amount);
}

export function addDays(date: Date, amount: number) {
	return addDaysFns(date, amount);
}

export function addMonths(date: Date, amount: number) {
	return addMonthsFns(date, amount);
}

export function addYears(date: Date, amount: number) {
	return addYearsFns(date, amount);
}

export function getDifferenceInMinutes(from: Date, to: Date) {
	return differenceInMinutes(to, from);
}

export function getQuarter(date: Date) {
	return getQuarterFns(date);
}

export function formatToTodayString(date: Date) {
	if (!date) return null;

	if (isToday(date)) {
		return t('DATE_TIME.TODAY_AT', { defaultValue: 'today at {{time}}', time: format(date, 'HH:mm') });
	} else if (isYesterday(date)) {
		return t('DATE_TIME.YESTERDAY_AT', { defaultValue: 'yesterday at {{time}}', time: format(date, 'HH:mm') });
	} else {
		return format(date, 'yyyy-MM-dd HH:mm');
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExcludeArrayKeys<T> = Exclude<T, keyof any[]>;

export function transformDateStringPropsToDate<T, K extends ExcludeArrayKeys<keyof T>>(obj: T, datePropKeys: K[]): T;
export function transformDateStringPropsToDate<T, K extends ExcludeArrayKeys<keyof T>>(obj: T[], datePropKeys: K[]): T[];
export function transformDateStringPropsToDate<T, K extends ExcludeArrayKeys<keyof T>>(obj: T | T[], datePropKeys: K[]): T | T[] {
	if (obj === null || obj === undefined) {
		return obj;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => transformDateStringPropsToDate(item, datePropKeys)) as T[];
	} else {
		datePropKeys.forEach((dpk) => {
			const value = obj[dpk];
			if (typeof value === 'string' && value.trim() !== '' && isISO8601(value)) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				obj[dpk] = new Date(value) as any;
				//obj[dpk] = (value.includes('T') ? new Date(value) : new Date(obj + 'T00:00:00Z')) as any;
			}
		});

		return obj;
	}
}

function isISO8601(str: string): boolean {
	//const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:.\d{1,7})?Z$/;
	const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})?$/;
	return regex.test(str);
}
